import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styles from './Services.module.css';

const services = [
  {
    number: '01',
    title: 'Custom Murals',
    description:
      'Unique, large-scale wall paintings tailored for schools, businesses, restaurants, and homes. From initial concept to final brushstroke, each mural is a one-of-a-kind statement that transforms any space into an immersive experience.',
    details: ['Schools & Education Spaces', 'Commercial & Retail', 'Private Residences', 'Feature Walls & Lobbies'],
    icon: '◈',
  },
  {
    number: '02',
    title: 'Canvas & Studio Art',
    description:
      'Original oil and acrylic paintings on canvas, crafted for personal collections or commercial interiors. Each piece reflects a deep observational practice and technical mastery developed over decades of formal training.',
    details: ['Oil & Acrylic Originals', 'Custom Size & Format', 'Interior-Focused Pieces', 'Reproduction Reinterpretations'],
    icon: '◉',
  },
  {
    number: '03',
    title: 'Surface & Textile Design',
    description:
      'Custom textile patterns and decorative surface designs inspired by natural textures, organic forms, and environmental observation. From fabric prints to wallpaper and decorative finishes, every pattern carries a story.',
    details: ['Fabric & Textile Prints', 'Wallpaper & Surface Patterns', 'Decorative Finishes', 'Repeat Pattern Design'],
    icon: '◫',
  },
  {
    number: '04',
    title: 'Commissioned Artwork',
    description:
      'Tailored artworks made to fit your vision, space, and aesthetic. Whether a portrait, landscape, or abstract piece, Bilge works closely with each client to bring their ideas to life with precision, warmth, and artistic depth.',
    details: ['Portrait Commissions', 'Landscape & Abstract', 'Gift Artworks', 'Collaborative Design Process'],
    icon: '◍',
  },
];

export default function Services() {
  const [active, setActive] = useState(null);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="services" className={styles.services} ref={ref}>
      <div className={styles.container}>
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9 }}
        >
          <span className={styles.sectionLabel}>
            <span className={styles.labelLine} /> What I Offer
          </span>
          <h2 className={styles.heading}>
            Services Crafted for{' '}
            <em>Meaningful Spaces</em>
          </h2>
          <p className={styles.subtext}>
            Every project begins with a conversation. Whether you have a clear vision or a blank wall,
            the result will be something entirely yours.
          </p>
        </motion.div>

        <div className={styles.grid}>
          {services.map((service, i) => (
            <motion.div
              key={service.number}
              className={`${styles.card} ${active === i ? styles.cardActive : ''}`}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.1 + i * 0.12 }}
              onClick={() => setActive(active === i ? null : i)}
            >
              <div className={styles.cardTop}>
                <span className={styles.cardNum}>{service.number}</span>
                <span className={styles.cardIcon}>{service.icon}</span>
              </div>
              <h3 className={styles.cardTitle}>{service.title}</h3>
              <p className={styles.cardDesc}>{service.description}</p>
              <ul className={styles.cardDetails}>
                {service.details.map(d => (
                  <li key={d}>
                    <span className={styles.bullet} />
                    {d}
                  </li>
                ))}
              </ul>
              <div className={styles.cardCta}>
                <a
                  href="https://bookanartist.co/artist/artist-for-hire/13184"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                >
                  Book This Service →
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
